// HqGob — the structure builder: one-at-a-time queue, structures created in-world with
// kfGobBeingBuilt at the command position (the BUGBUG lag-occupancy note is the C++'s),
// scorch cleanup under fresh construction, kmidBuildComplete handoff on completion.
// game/Headquarters.cpp:27-313 (the rest of the file is the build/place forms — UI).

import type { SimContext } from '../context.ts';
import { BuilderGob } from './builder.ts';
import { StructGob } from './struct.ts';
import {
  BuilderConsts,
  StructConsts,
  kfUntcStructureBuilder,
  InitBuilderConstsClass,
} from './unitconsts.ts';
import { kgtHeadquarters, kgtScorch } from './gobtypes.ts';
import { kutHeadquarters } from '../player/side.ts';
import { kumStructures, kumHeadquarters, kumReplicator } from '../player/masks.ts';
import { kfGobBeingBuilt } from './gob.ts';
import {
  Message,
  knHandled,
  kstReservedGlobal,
  kmidReservedEnter,
  kmidReservedExit,
  kmidReservedUpdate,
  kmidBuildOtherCommand,
  kmidAbortBuildOtherCommand,
  kmidSelfDestructCommand,
  kmidBuildComplete,
  kgidNull,
  kstIdle,
  kstBuildOtherCompleting,
} from '../sm/message.ts';
import { knLimitStruct } from './gobmgr.ts';
import { CreateGob } from './factory.ts';
import { TRect } from '../types.ts';
import type { IniReader } from '../data/ini.ts';
import {
  ksfxHeadquartersAbortConstruction,
  ksfxHeadquartersConstruct,
  ksfxHeadquartersStructureReady,
  ksfxHeadquartersAbortRepair,
  ksfxHeadquartersRepair,
  ksfxHeadquartersDamaged,
  ksfxHeadquartersDestroyed,
  ksfxHeadquartersSelect,
} from '../data/soundeffects.ts';
import type { Player } from '../player/player.ts';

// game/Headquarters.cpp:41-94 — gbldcHq (forms/bitmaps/sfx are host)
export function InitHqClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new BuilderConsts().bind(ctx);
  c.gt = kgtHeadquarters;
  c.ut = kutHeadquarters;
  c.umCanBuild = (kumStructures & ~(kumHeadquarters | kumReplicator)) >>> 0;
  c.wf |= kfUntcStructureBuilder;
  // Sound effects (Headquarters.cpp:84-91)
  c.sfxUnitBuildAbort = ksfxHeadquartersAbortConstruction;
  c.sfxUnitBuild = ksfxHeadquartersConstruct;
  c.sfxUnitReady = ksfxHeadquartersStructureReady;
  c.sfxAbortRepair = ksfxHeadquartersAbortRepair;
  c.sfxRepair = ksfxHeadquartersRepair;
  c.sfxDamaged = ksfxHeadquartersDamaged;
  c.sfxDestroyed = ksfxHeadquartersDestroyed;
  c.sfxSelect = ksfxHeadquartersSelect;
  return InitBuilderConstsClass(ctx, c, pini); // BuilderGob::InitClass(&gbldcHq, pini)
}

export class HqGob extends BuilderGob {
  // game/Headquarters.cpp:110-113 — no queuing
  constructor(ctx: SimContext) {
    super(ctx, ctx.unitConsts[kutHeadquarters] as BuilderConsts);
    this.m_bq.SetSize(1);
  }

  // game/Headquarters.cpp:175-186 — take down the build and place forms (host
  // seam), HQ is no longer in working order
  override Deactivate(): void {
    super.Deactivate();
    this.ctx.host.BuilderDeactivate(this);
  }

  // game/Headquarters.cpp:199-239 — clear scorches under + 1 tile around new construction
  RemoveScorch(pstru: StructGob): void {
    const trc = new TRect();
    pstru.GetTileRect(trc);
    trc.left -= 1;
    trc.top -= 1;
    trc.right += 1;
    trc.bottom += 1; // Inflate(1,1)

    if (trc.left < 0) trc.left = 0;
    if (trc.top < 0) trc.top = 0;
    const { ctx: cxMap, cty: cyMap } = this.ctx.gobm.GetMapSize();
    if (trc.right > cxMap) trc.right = cxMap;
    if (trc.bottom > cyMap) trc.bottom = cyMap;

    for (let ty = trc.top; ty < trc.bottom; ty++) {
      for (let tx = trc.left; tx < trc.right; tx++) {
        let gid = this.ctx.gobm.GetFirstGid(tx, ty);
        while (gid !== kgidNull) {
          const gidNext = this.ctx.gobm.GetNextGid(gid);
          const pgob = this.ctx.gobm.GetGob(gid, false);
          if (pgob !== null && pgob.GetType() === kgtScorch) {
            this.ctx.gobm.RemoveGob(pgob);
          }
          gid = gidNext;
        }
      }
    }
  }

  // game/Headquarters.cpp:241-313
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal) {
      switch (pmsg.mid) {
        case kmidBuildOtherCommand:
          // One at a time; the bedrock limit decision happens HERE on every client
          if (this.m_bq.IsEmpty()) {
            if (this.ctx.gobm.IsBelowLimit(knLimitStruct, this.m_pplr as Player)) {
              const pstru = CreateGob(
                this.ctx,
                (this.ctx.unitConsts[pmsg.BuildOtherCommand.ut] as StructConsts).gt,
              ) as StructGob | null;
              if (pstru === null) return knHandled;
              pstru.Init(
                pmsg.BuildOtherCommand.wpt.wx,
                pmsg.BuildOtherCommand.wpt.wy,
                this.m_pplr as Player,
                0,
                kfGobBeingBuilt,
              );
              pstru.SetHealth(0);

              this.RemoveScorch(pstru);

              this.Build(pmsg.BuildOtherCommand.ut, pstru.GetId());
            }
          }
          return knHandled;

        case kmidAbortBuildOtherCommand:
          this.AbortBuild(true);
          return knHandled;

        case kmidSelfDestructCommand:
          this.AbortBuild(true); // refund before the recycle
          this.SelfDestruct();
          return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstBuildOtherCompleting) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          // (confirmation takedown: forms UI)

          this.ctx.smm!.SendMsgIds(kmidBuildComplete, this.m_gidBuildVisible);

          this.ctx.sim!.GetBuildMgr()?.OnBuilt?.(this.GetBuiltGob(), this);

          this.ClearBuiltGob();
          this.SetState(kstIdle);
          return knHandled;

        // Eat Update/Exit so they don't route up to BuilderGob's handlers
        case kmidReservedUpdate:
        case kmidReservedExit:
          return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    return super.ProcessStateMachineMessage(st, pmsg);
  }
}

// self-register (see factory.ts)
import { registerGobType } from './factory.ts';
registerGobType(kgtHeadquarters, (ctx) => new HqGob(ctx));
